import { createFileRoute, Link } from "@tanstack/react-router";
import portrait from "@/assets/arda-event-portrait.webp.asset.json";
import landscape from "@/assets/arda-event-landscape.webp.asset.json";
import { BOOKS } from "@/components/site/theme-ui";
import { WRAP } from "@/components/site/page";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { getAllPosts } from "@/lib/posts.functions";
import { listApprovedThemes } from "@/lib/themes-public.functions";
import { themeByKey } from "@/lib/themes-public";
import { talks, formatTalkDate } from "@/lib/talks";
import { AUTHOR_BOOKS, AUTHOR_URL, BOARDS, COLUMN_URL, CURRENT_WORK, EMAIL, LINKEDIN_URL, personSchema } from "@/content/author";

const TITLE = "Arda Öztaşkın Kimdir? Yazar, Dört Kitap ve Konuşmalar";
const DESC =
  "Arda Öztaşkın kimdir? İklim, yapay zekâ, ekonomi ve insan üzerine yazan yazar: 2023'ten bu yana dört kitap, Ekonomim köşesi, konuşma kayıtları ve üniversite dersleri tek sayfada.";

export const Route = createFileRoute("/yazar")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: AUTHOR_URL },
      { property: "og:type", content: "profile" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: AUTHOR_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({ "@context": "https://schema.org", "@type": "ProfilePage", url: AUTHOR_URL, inLanguage: "tr-TR", mainEntity: personSchema() }),
      },
    ],
  }),
  loader: async () => {
    const [posts, themes] = await Promise.all([getAllPosts({ data: { lang: "tr" } }), listApprovedThemes()]);
    const map = new Map(themes.map((t) => [t.slug, t.primary]));
    const latest = [...posts]
      .sort((a, b) => (a.isoDate < b.isoDate ? 1 : -1))
      .slice(0, 3)
      .map((p) => ({ slug: p.slug, title: p.title, date: p.date, theme: themeByKey(map.get(p.slug))?.title ?? null }));
    return { latest, total: posts.length };
  },
  component: AuthorHub,
});

const SECTIONS = [
  { id: "yaziyor", n: "01", label: "Yazıyor" },
  { id: "kitaplar", n: "02", label: "Kitaplar" },
  { id: "konusuyor", n: "03", label: "Konuşuyor" },
  { id: "katki", n: "04", label: "Katkı veriyor" },
];

const LINK = "inline-flex min-h-11 items-center border-b border-theme-tech/40 text-[14px] text-theme-tech hover:border-theme-tech";

function Section({ id, n, title, intro, children }: { id: string; n: string; title: string; intro: string; children: React.ReactNode }) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="scroll-mt-16 border-b border-ink/10 py-10 lg:grid lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-12 lg:py-14">
      <div>
        <span className="block text-[13px] text-clay">{n}</span>
        <h2 id={`${id}-h`} className="mt-1 font-serif text-[26px] font-medium leading-tight lg:text-[30px]">{title}</h2>
      </div>
      <div className="mt-3 min-w-0 lg:mt-0">
        <p className="mb-6 max-w-[46ch] font-serif text-[17px] leading-snug text-ink/85 lg:text-[19px]">{intro}</p>
        {children}
      </div>
    </section>
  );
}

function AuthorHub() {
  const { latest, total } = Route.useLoaderData();
  const [featured, ...rest] = talks;
  const recent = rest.slice(0, 3);

  return (
    <div className={WRAP}>
      <div className="pt-5 lg:pt-7">
        <Breadcrumbs items={[{ label: "Yazar" }]} />
      </div>

      <section className="grid gap-6 pb-8 pt-5 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:items-end lg:gap-14 lg:pb-12 lg:pt-8">
        <div className="order-2 lg:order-1">
          <p className="text-[11px] uppercase tracking-[0.2em] text-ink/70">Yazar</p>
          <h1 className="mt-2 font-serif text-[40px] font-medium leading-[1.02] sm:text-[56px] lg:text-[64px]">Arda Öztaşkın</h1>
          <div className="mt-5 max-w-[34ch] space-y-4 font-serif text-[19px] leading-snug text-ink/80 sm:text-[22px]">
            <p>İklim, yapay zekâ, ekonomi, yalnızlık ve anlam arayışı üzerine yazıyorum. Bunları ayrı konular olarak görmüyorum; hepsi, kurduğumuz dünyanın bizi nasıl değiştirdiğine farklı yerlerden bakıyor.</p>
            <p className="text-[17px] text-ink/70 sm:text-[19px]">Yazılarımı bu sitede ve Ekonomim'deki köşemde yayımlıyorum. Kitaplarda aynı sorulara daha uzun süre kalarak dönüyorum.</p>
          </div>
        </div>
        <picture className="order-1 lg:order-2">
          <source media="(min-width: 1024px)" srcSet={portrait.url} />
          <source media="(min-width: 640px)" srcSet={landscape.url} />
          <img
            src={portrait.url}
            alt="Arda Öztaşkın bir panelde konuşurken"
            width={768}
            height={960}
            loading="eager"
            fetchPriority="high"
            className="aspect-[4/5] w-full object-cover object-center sm:aspect-[4/3] sm:max-h-[30rem] sm:object-[center_20%] lg:ml-auto lg:aspect-[4/5] lg:max-h-none lg:max-w-[24rem] lg:object-center"
          />
        </picture>
      </section>

      <nav aria-label="Bölümler" className="sticky top-0 z-10 -mx-5 border-y border-ink/15 bg-paper px-5 sm:mx-0 sm:px-0">
        <ul className="flex justify-between overflow-x-auto whitespace-nowrap sm:justify-start sm:gap-6">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="inline-flex min-h-12 items-center gap-1.5 text-[14px] font-medium">
                <span className="hidden text-ink/50 sm:inline">{s.n}</span>
                {s.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <Section id="yaziyor" n="01" title="Yazıyor" intro="İklim, teknoloji, ekonomi ve insan üzerine yazıyorum. Güncel gelişmelerin arkasındaki daha büyük soruları anlamaya, farklı alanlar arasında çoğu zaman gözden kaçan bağlantıları kurmaya çalışıyorum.">
        <ul>
          {latest.map((p, i) => (
            <li key={p.slug} className="border-t border-ink/10">
              <Link to="/yazilar/$slug" params={{ slug: p.slug }} className="group grid gap-1 py-4 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-6">
                <span className="text-[12px] text-ink/65 sm:pt-1.5">
                  {p.date}
                  {p.theme && <span className="sm:block"><span className="sm:hidden"> · </span>{p.theme}</span>}
                </span>
                <span className={`font-serif leading-tight group-hover:text-clay ${i === 0 ? "text-[23px] sm:text-[30px]" : "text-[18px] sm:text-[21px]"}`}>{p.title}</span>
              </Link>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-col gap-x-10 sm:flex-row">
          <Link to="/yazilar" className={LINK}>Tüm yazılar ({total}) →</Link>
          <a href={COLUMN_URL} target="_blank" rel="noopener noreferrer" className={LINK}>Ekonomim'deki köşe: Akıllı Sürdürülebilirlik ↗</a>
        </div>
      </Section>

      <Section id="kitaplar" n="02" title="Kitaplar" intro="2023'ten bu yana dört kitap yazdım. Her biri, bir öncekini bitirirken aklımda kalan soruyla başladı.">
        <p className="mb-3 text-[12px] text-ink/60 sm:hidden">Dört kitap · yana kaydırın →</p>
        <ul className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 sm:mx-0 sm:grid sm:grid-cols-4 sm:gap-6 sm:overflow-visible sm:px-0">
          {AUTHOR_BOOKS.map((b) => (
            <li key={b.id} className="w-[44%] shrink-0 snap-start sm:w-auto">
              <Link to={b.to} className="group block">
                <img src={BOOKS[b.id].cover} alt={`${b.title} kitap kapağı`} width={300} height={450} loading="lazy" className="aspect-[2/3] w-full object-cover shadow-[0_14px_28px_-16px_rgb(0_0_0/0.45)]" />
                <p className="mt-3 text-[12px] text-ink/65">{b.year} · {b.publisher}</p>
                <p className="mt-0.5 font-serif text-[16px] font-medium leading-snug group-hover:text-clay sm:text-[17px]">{b.title}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-ink/75">{b.blurb}</p>
              </Link>
            </li>
          ))}
        </ul>
        <div className="mt-4"><Link to="/kitaplar" className={LINK}>Kitaplar sayfası →</Link></div>
      </Section>

      <Section id="konusuyor" n="03" title="Konuşuyor" intro={`Konferans, panel ve söyleşilerde yaptığım konuşmaların kayıtları; 2022'den bu yana ${talks.length} kayıt.`}>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] lg:gap-8">
          {featured && (
            <a href={featured.url} target="_blank" rel="noopener noreferrer" className="group block">
              <img src={`https://i.ytimg.com/vi/${featured.id}/hqdefault.jpg`} alt={`${featured.topic} konuşmasından video karesi`} width={480} height={270} loading="lazy" className="aspect-video w-full object-cover" />
              <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-ink/65">{formatTalkDate(featured.date)} · {featured.program}</p>
              <p className="mt-1 font-serif text-[21px] leading-snug group-hover:text-clay">{featured.topic}</p>
            </a>
          )}
          <ul>
            {recent.map((t) => (
              <li key={t.id} className="border-t border-ink/10">
                <a href={t.url} target="_blank" rel="noopener noreferrer" className="group block py-3">
                  <span className="block text-[12px] text-ink/65">{formatTalkDate(t.date)}</span>
                  <span className="block font-serif text-[16px] leading-snug group-hover:text-clay">{t.topic}</span>
                  <span className="block text-[12px] text-ink/65">{t.program}</span>
                </a>
              </li>
            ))}
            <li className="border-t border-ink/10 pt-1"><Link to="/konusmalar" className={LINK}>Tüm konuşmalar ({talks.length}) →</Link></li>
          </ul>
        </div>
      </Section>

      <Section id="katki" n="04" title="Katkı veriyor" intro="Üniversitede ders veriyorum; iletişim ve sürdürülebilirlik alanındaki meslek kuruluşlarının çalışmalarında yer alıyorum.">
        <div className="grid gap-8 sm:grid-cols-2 sm:gap-10">
          <div>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-clay">Ders</h3>
            <ul>
              {CURRENT_WORK.filter((r) => r.org.includes("Üniversite")).map((r) => (
                <li key={r.org} className="border-t border-ink/10 py-3">
                  <p className="font-serif text-[17px] leading-snug">{r.role}</p>
                  <p className="text-[13px] text-ink/70">{r.org}{r.when ? ` · ${r.when}` : ""}</p>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-clay">Dernek ve kurullar</h3>
            <ul>
              {BOARDS.map((r) => (
                <li key={r.org} className="border-t border-ink/10 py-3">
                  <p className="font-serif text-[17px] leading-snug">{r.role}</p>
                  <p className="text-[13px] text-ink/70">{r.org}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-4"><Link to="/hakkimda" className={LINK}>Biyografi ve çalışmalar →</Link></div>
      </Section>

      <section aria-label="İletişim" className="py-8 pb-14 text-[15px] text-ink/80">
        Konuşma, söyleşi ve yazı teklifleri için{" "}
        <a href={`mailto:${EMAIL.user}@${EMAIL.domain}`} className="text-theme-tech underline">{EMAIL.user}@{EMAIL.domain}</a>
        {" · "}
        <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer me" className="text-theme-tech underline">LinkedIn</a>
      </section>
    </div>
  );
}
